import { streamTextLines } from "@/routes/environments/log-streams/log-groups";
import { streamWithCache } from "@/utils/stream-cache";
import { getConversationCacheKey } from "@/utils/dashboard-conversation-stream";
import type { JsonValue, PlainMessage } from "@bufbuild/protobuf";
import {
    AgentResponseBlock,
    AgentResponseBlock_Phase,
    AgentResponseBlock_TextOutput_Type,
    AgentResponseBlock_FileModification_DiffGroup_Type,
    AgentResponseBlock_AgentModeChange_State,
    AgentMessage as AgentMessageProto,
    UserInputBlock,
    UserInputBlock_TextInput,
    type AgentMessage_Type,
    type AgentMessage_Role,
    type AgentResponseBlock_ActionCompleted,
    type AgentResponseBlock_TodoItem_Phase,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCreateAgentExecutionConversationToken } from "@/queries/agent-queries";

const ERROR_DISPLAY_THRESHOLD = 3;

/**
 * Strips ONA_SPEC_COMPLETE marker from text content.
 * This is a defensive fallback - the backend also strips this marker,
 * but streaming race conditions may cause it to slip through.
 * Returns null if content is empty after stripping.
 */
export function stripSpecCompleteMarker(content: string): string | null {
    // Check if entire content is just the marker
    if (content.trim() === "ONA_SPEC_COMPLETE") {
        return null;
    }
    // Strip the marker if it appears on its own line within larger content
    const stripped = content
        .replace(/\nONA_SPEC_COMPLETE\n/g, "\n")
        .replace(/\nONA_SPEC_COMPLETE$/g, "")
        .replace(/^ONA_SPEC_COMPLETE\n/g, "");
    // Return null if content is empty after stripping
    if (stripped.trim() === "") {
        return null;
    }
    return stripped;
}

export type ConversationBlock =
    | {
          id: string;
          type: "user";
          content: PlainMessage<UserInputBlock>;
      }
    | {
          id: string;
          type: "agent";
          content: PlainMessage<AgentResponseBlock>;
      }
    | {
          id: string;
          type: "agentMsg";
          content: PlainMessage<AgentMessageProto>;
      };

export type Message = AgentMessage | UserMessage;

/**
 * Represents a message that has been submitted, but hasn't yet been recorded in the conversion.
 */
export type PendingMessage = {
    // This is a client-defined ID which the backend will use for the block ID in the conversation
    id: string;
    content: string;
};

/**
 * Type guard to check if a Message is an AgentResponse
 */
export function isAgentResponse(message?: Message): message is AgentMessage {
    return message?.type === "agent";
}

/**
 * Type guard to check if a Message is a UserMessage
 */
export function isUserMessage(message?: Message): message is UserMessage {
    return message?.type === "user";
}

/**
 * A UserMessage can include multiple user inputs
 * It represents a full "turn" in the conversation.
 */
export type UserMessage = {
    id: string;
    type: "user";
    fragments: UserMessageFragment[];
};

/**
 * This is similar to UserInputBlock_<XYZ>Input
 */
export type UserMessageFragment = UserMessageFragmentText | UserMessageFragmentImage | UserMessageFragmentAgentMessage;

export type UserMessageFragmentText = {
    type: "text";
    id: string;
    content: string;
};

export type UserMessageFragmentImage = {
    type: "image";
    id: string;
    data: Uint8Array;
    mimeType: string;
    /** When set, the image should be fetched from this URL instead of using inline data. */
    blobUrl?: string;
};

export type UserMessageFragmentAgentMessage = {
    type: "agentMessage";
    id: string;
    payload: string;
    messageType: AgentMessage_Type;
    role: AgentMessage_Role;
    senderExecutionId: string;
};

/**
 * An AgentMessage can include multiple agent response such as text, tool use, etc.
 * It represents a full "turn" in the conversation.
 */
export type AgentMessage = {
    id: number;
    type: "agent";
    fragments: AgentMessageFragment[];
    injected?: boolean;
};

// Tag fields present on all agent fragments in snapshot mode.
// Empty string when not in snapshot mode or not set.
export type TodoTagFields = {
    todoGroupId: string;
    todoItemId: string;
};

/**
 * This is similar to AgentResponseBlock_<XYZ>Output, but where actionStarted and actionCompleted are merged into a single type
 * as that simplifies the UI logic.
 */
export type AgentMessageFragment = (
    | AgentMessageFragmentText
    | AgentMessageFragmentAction
    | AgentMessageFragmentFileModification
    | AgentMessageFragmentEnvironmentCreation
    | AgentMessageFragmentThinking
    | AgentMessageFragmentHostAuthenticationRequired
    | AgentMessageFragmentTodoItem
    | AgentMessageFragmentTodoGroup
    | AgentMessageFragmentUserInputSeen
    | AgentMessageFragmentCodeAnnotation
    | AgentMessageFragmentAgentModeChange
    | AgentMessageFragmentClarifyingQuestions
    | AgentMessageFragmentNextStepsProposal
) & Partial<TodoTagFields>;

export type AgentMessageFragmentType = AgentMessageFragment["type"];

export type AgentMessageFragmentText = {
    type: "text";
    id: string;
    content: string;
    completed: boolean;
};

export type AgentMessageFragmentThinking = {
    type: "thinking";
    id: string;
    content: string;
    completed: boolean;
};

export type AgentMessageFragmentFileModification = {
    type: "fileModification";
    id: string;
    actionId: string;
    filePath: string;
    content: string;
    // Enhanced diff format from the agent
    diff?: {
        diffGroups: {
            type: "added" | "removed" | "unchanged";
            lines: string[];
        }[];
        stats: {
            additions: number;
            deletions: number;
        };
    };
};

export type AgentMessageFragmentEnvironmentCreation = {
    type: "environmentCreation";
    id: string;
    actionId: string;
    environmentId: string;
};

export type AgentMessageFragmentAction = {
    type: "action";
    id: string;
    actionId: string;
    state: "running" | "completed" | "failed";
    intentDescription: string;
    resultDescription: string;
};

export type AgentMessageFragmentHostAuthenticationRequired = {
    type: "hostAuthenticationRequired";
    id: string;
    actionId: string;
    host: string;
    runnerId: string;
};

export type AgentMessageFragmentTodoItem = {
    type: "todoItem";
    id: string;
    todoItemId: string;
    // If the phase is cleared, then the title is not set
    title?: string;
    status: AgentResponseBlock_TodoItem_Phase;
};

export type AgentMessageFragmentTodoGroup = {
    type: "todoGroup";
    id: string;
    groupId: string;
    todos: {
        id: string;
        title: string;
        phase: AgentResponseBlock_TodoItem_Phase;
    }[];
};

export type AgentMessageFragmentUserInputSeen = {
    type: "userInputSeen";
    id: string;
    value: {
        id: string;
    };
};

export type AgentMessageFragmentCodeAnnotation = {
    type: "codeAnnotation";
    id: string;
    filePath: string;
    startLine: number;
    endLine: number;
    description: string;
    title: string;
};

export type AgentModeChangeState = "specComplete" | "iterationComplete" | "complete" | "onaSpecComplete";

export type AgentMessageFragmentAgentModeChange = {
    type: "agentModeChange";
    id: string;
    state: AgentModeChangeState;
    message: string;
};

export type ClarifyingQuestionChoice = {
    label: string;
    text: string;
    description?: string;
};

export type ClarifyingQuestion = {
    questionId: string;
    question: string;
    choices: ClarifyingQuestionChoice[];
    allowOther: boolean;
};

export type AgentMessageFragmentClarifyingQuestions = {
    type: "clarifyingQuestions";
    id: string;
    questions: ClarifyingQuestion[];
};

export type NextStepAction = {
    title: string;
    prompt: string;
    variant: "primary" | "secondary";
};

export type AgentMessageFragmentNextStepsProposal = {
    type: "nextStepsProposal";
    id: string;
    actions: NextStepAction[];
};

export type LastBlockActivityContext = {
    id?: string;
    status?: string;
    timestamp: number;
};

/**
 * Type guard to check if a fragment is an AgentResponseFragmentAction
 */
function isAgentResponseFragmentAction(fragment: AgentMessageFragment): fragment is AgentMessageFragmentAction {
    return fragment.type === "action";
}

/**
 * Derives a blob URL from the conversation URL and a data_ref (SHA-256 hash key).
 * Conversation URL: "{base}/{execID}/conversation"
 * Blob URL:         "{base}/{execID}/conversation/blobs/{dataRef}"
 */
export function deriveBlobUrl(conversationUrl: string, dataRef: string): string | undefined {
    if (!dataRef) return undefined;
    return `${conversationUrl}/blobs/${dataRef}`;
}

/**
 * Converts a list of ConversationBlocks into a list of Messages suitable for rendering.
 * The order of the blocks is important, as it influces how the blocks are converted into messages.
 */
export function convertToMessages(blocks: ConversationBlock[], conversationUrl?: string): Message[] {
    const messages: Message[] = [];
    const seenUserInputIds = new Set<string>();

    if (blocks.length === 0) {
        return messages;
    }

    let currentMessage: Message | undefined;
    // The backend (plan_interaction.go DescribeFunc) emits a structured block
    // (clarifyingQuestions or nextStepsProposal) immediately followed by a
    // plain-text fallback block within the same tool response. The fallback
    // is for clients that don't render the structured UI. We skip it here
    // because we render the structured component instead.
    // Invariant: the fallback text block is always the next block in the stream
    // after the structured block, emitted as part of the same DescribeFunc return.
    let skipNextTextBlock = false;

    for (const block of blocks) {
        if (block.type === "user" && block.content.id) {
            if (seenUserInputIds.has(block.content.id)) {
                console.warn("Duplicate user input block filtered in UI", {
                    blockId: block.id,
                    userInputId: block.content.id,
                    detectionPoint: "ui_rendering",
                });
                continue;
            }
            seenUserInputIds.add(block.content.id);
        }
        // Determine the effective message type for turn-change detection.
        // agentMsg blocks are rendered as user messages (input from a subagent).
        const effectiveType = block.type === "agentMsg" ? "user" : block.type;

        // If the block type is different from the current message type, we need to create a new message
        // as it indicates the "turn" has changed.
        if (currentMessage && effectiveType !== currentMessage?.type) {
            messages.push(currentMessage);
            currentMessage = undefined;
        }

        // Each agentMsg block is a distinct message from a subagent, so always start a new message.
        if (block.type === "agentMsg") {
            if (currentMessage) {
                messages.push(currentMessage);
            }
            currentMessage = {
                type: "user",
                id: `message-${messages.length + 1}`,
                fragments: [
                    {
                        type: "agentMessage",
                        id: block.id,
                        payload: block.content.payload,
                        messageType: block.content.type,
                        role: block.content.role,
                        senderExecutionId: block.content.senderExecutionId,
                    },
                ],
            };
            // Push immediately — each agentMsg is its own message bubble
            messages.push(currentMessage);
            currentMessage = undefined;
            continue;
        }

        if (block.type === "user") {
            if (!currentMessage) {
                currentMessage = {
                    type: "user",
                    id: `message-${messages.length + 1}`,
                    fragments: [],
                };
            }

            if (isUserMessage(currentMessage)) {
                // Handle new inputs array format
                if (block.content.inputs && block.content.inputs.length > 0) {
                    for (const input of block.content.inputs) {
                        switch (input.input.case) {
                            case "text":
                                currentMessage.fragments.push({
                                    type: "text",
                                    id: `${block.id}-text-${currentMessage.fragments.length}`,
                                    content: input.input.value.content,
                                });
                                break;
                            case "image": {
                                const imgValue = input.input.value;
                                const fragment: UserMessageFragmentImage = {
                                    type: "image",
                                    id: `${block.id}-image-${currentMessage.fragments.length}`,
                                    data: imgValue.data,
                                    mimeType: imgValue.mimeType,
                                };
                                if (imgValue.dataRef && conversationUrl) {
                                    fragment.blobUrl = deriveBlobUrl(conversationUrl, imgValue.dataRef);
                                }
                                currentMessage.fragments.push(fragment);
                                break;
                            }
                        }
                    }
                } else {
                    // Handle deprecated input field for backwards compatibility
                    switch (block.content.input.case) {
                        case "text":
                            currentMessage.fragments.push({
                                type: "text",
                                id: block.id,
                                content: block.content.input.value.content,
                            });
                            break;
                        case "image": {
                            const imgValue = block.content.input.value;
                            const fragment: UserMessageFragmentImage = {
                                type: "image",
                                id: block.id,
                                data: imgValue.data,
                                mimeType: imgValue.mimeType,
                            };
                            if (imgValue.dataRef && conversationUrl) {
                                fragment.blobUrl = deriveBlobUrl(conversationUrl, imgValue.dataRef);
                            }
                            currentMessage.fragments.push(fragment);
                            break;
                        }
                    }
                }
            }
        }

        if (block.type === "agent") {
            if (!currentMessage) {
                currentMessage = {
                    type: "agent",
                    id: messages.length + 1,
                    fragments: [],
                };
            }

            if (isAgentResponse(currentMessage)) {
                switch (block.content.output.case) {
                    case "userInputSeen":
                        currentMessage.fragments.push({
                            type: "userInputSeen",
                            id: block.id,
                            value: {
                                id: block.content.output.value.id,
                            },
                        });
                        break;
                    case "text": {
                        // Skip fallback text blocks that follow clarifyingQuestions or nextStepsProposal
                        if (skipNextTextBlock) {
                            skipNextTextBlock = false;
                            break;
                        }
                        const textContent = stripSpecCompleteMarker(block.content.output.value.content);
                        if (textContent === null) {
                            break;
                        }
                        if (block.content.output.value.type === AgentResponseBlock_TextOutput_Type.THOUGHTS) {
                            currentMessage.fragments.push({
                                type: "thinking",
                                id: block.id,
                                content: textContent,
                                completed: block.content.phase === AgentResponseBlock_Phase.COMPLETED,
                            });
                        } else {
                            currentMessage.fragments.push({
                                type: "text",
                                id: block.id,
                                content: textContent,
                                completed: block.content.phase === AgentResponseBlock_Phase.COMPLETED,
                            });
                        }
                        break;
                    }
                    case "actionStarted":
                        currentMessage.fragments.push({
                            type: "action",
                            id: block.id,
                            actionId: block.content.output.value.actionId,
                            state: "running",
                            intentDescription: block.content.output.value.intentDescription,
                            resultDescription: "",
                        });
                        skipNextTextBlock = false;
                        break;
                    case "actionCompleted":
                        markActionCompleted(currentMessage, block.content.output.value);
                        break;
                    case "fileModification": {
                        const fileModValue = block.content.output.value;
                        const fragment: AgentMessageFragmentFileModification = {
                            type: "fileModification",
                            id: block.id,
                            actionId: fileModValue.actionId,
                            filePath: fileModValue.filePath,
                            content: fileModValue.content,
                        };

                        // Include diff if available
                        if (fileModValue.diff) {
                            fragment.diff = {
                                diffGroups: fileModValue.diff.diffGroups.map((group) => ({
                                    type:
                                        group.type === AgentResponseBlock_FileModification_DiffGroup_Type.ADDED
                                            ? ("added" as const)
                                            : group.type === AgentResponseBlock_FileModification_DiffGroup_Type.REMOVED
                                              ? ("removed" as const)
                                              : ("unchanged" as const),
                                    lines: [...group.lines],
                                })),
                                stats: {
                                    additions: fileModValue.diff.stats?.additions ?? 0,
                                    deletions: fileModValue.diff.stats?.deletions ?? 0,
                                },
                            };
                        }

                        currentMessage.fragments.push(fragment);
                        break;
                    }
                    case "environmentCreation":
                        currentMessage.fragments.push({
                            type: "environmentCreation",
                            id: block.id,
                            actionId: block.content.output.value.actionId,
                            environmentId: block.content.output.value.environmentId,
                        });
                        break;
                    case "hostAuthenticationRequired":
                        currentMessage.fragments.push({
                            type: "hostAuthenticationRequired",
                            id: block.id,
                            actionId: block.content.output.value.actionId,
                            host: block.content.output.value.host,
                            runnerId: block.content.output.value.runnerId,
                        });
                        break;
                    case "todoItem": {
                        currentMessage.fragments.push({
                            type: "todoItem",
                            id: block.id,
                            todoItemId: block.content.output.value.id,
                            title: block.content.output.value.title,
                            status: block.content.output.value.phase,
                        });
                        break;
                    }
                    case "todoGroup": {
                        const groupValue = block.content.output.value;
                        currentMessage.fragments.push({
                            type: "todoGroup",
                            id: block.id,
                            groupId: groupValue.groupId,
                            todos: groupValue.todos.map((t) => ({
                                id: t.id,
                                title: t.title,
                                phase: t.phase,
                            })),
                        });
                        break;
                    }
                    case "codeAnnotation":
                        currentMessage.fragments.push({
                            type: "codeAnnotation",
                            id: block.id,
                            filePath: block.content.output.value.filePath,
                            startLine: block.content.output.value.startLine,
                            endLine: block.content.output.value.endLine,
                            description: block.content.output.value.description,
                            title: block.content.output.value.title || "",
                        });
                        break;
                    case "agentModeChange": {
                        const stateValue = block.content.output.value;
                        let state: AgentModeChangeState;
                        switch (stateValue.state) {
                            case AgentResponseBlock_AgentModeChange_State.SPEC_COMPLETE:
                                state = "specComplete";
                                break;
                            case AgentResponseBlock_AgentModeChange_State.ITERATION_COMPLETE:
                                state = "iterationComplete";
                                break;
                            case AgentResponseBlock_AgentModeChange_State.COMPLETE:
                                state = "complete";
                                break;
                            case AgentResponseBlock_AgentModeChange_State.ONA_SPEC_COMPLETE:
                                state = "onaSpecComplete";
                                break;
                            default:
                                state = "complete";
                        }
                        currentMessage.fragments.push({
                            type: "agentModeChange",
                            id: block.id,
                            state,
                            message: stateValue.message,
                        });
                        break;
                    }
                    case "clarifyingQuestions": {
                        const questionsValue = block.content.output.value;
                        currentMessage.fragments.push({
                            type: "clarifyingQuestions",
                            id: block.id,
                            questions: questionsValue.questions.map((q) => ({
                                questionId: q.questionId,
                                question: q.question,
                                choices: q.choices.map((c) => ({
                                    label: c.label,
                                    text: c.text,
                                    description: c.description || undefined,
                                })),
                                allowOther: q.allowOther,
                            })),
                        });
                        // Skip the fallback text block that follows
                        skipNextTextBlock = true;
                        break;
                    }
                    case "nextStepsProposal": {
                        const proposalValue = block.content.output.value;
                        currentMessage.fragments.push({
                            type: "nextStepsProposal",
                            id: block.id,
                            actions: proposalValue.actions.map((a) => ({
                                title: a.title,
                                prompt: a.prompt,
                                variant: a.variant === "primary" ? "primary" : "secondary",
                            })),
                        });
                        // Skip the fallback text block that follows
                        skipNextTextBlock = true;
                        break;
                    }
                }

                // Propagate snapshot-mode todo tags to the fragment just pushed
                const lastFragment = currentMessage.fragments[currentMessage.fragments.length - 1];
                if (lastFragment && block.content.todoGroupId) {
                    lastFragment.todoGroupId = block.content.todoGroupId;
                    lastFragment.todoItemId = block.content.todoItemId;
                }
            }
        }
    }

    if (currentMessage) {
        messages.push(currentMessage);
    }

    return messages;
}

function markActionCompleted(currentMessage: AgentMessage, block: PlainMessage<AgentResponseBlock_ActionCompleted>) {
    const action: AgentMessageFragmentAction | undefined = currentMessage.fragments.find(
        (f): f is AgentMessageFragmentAction => isAgentResponseFragmentAction(f) && f.actionId === block.actionId,
    );
    if (action) {
        action.state = block.failed ? "failed" : "completed";
        action.resultDescription = block.resultDescription;
    }
}

export async function* parseNDJSON(
    stream: ReadableStream<Uint8Array>,
    signal: AbortSignal,
): AsyncGenerator<ConversationBlock[], void, void> {
    let index = 0;
    for await (const lines of streamTextLines(stream, signal)) {
        const blocks: ConversationBlock[] = [];
        for (const line of lines) {
            const block = parseConversationBlock(index++, line);
            if (block) {
                blocks.push(block);
            }
        }
        yield blocks;
    }
}

export function parseConversationBlock(index: number, line: string): ConversationBlock | null {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const block = JSON.parse(line);
        if (block.id && "phase" in block) {
            if ("userInput" in block) {
                return {
                    id: `input-${block.id}`,
                    type: "user",
                    content: UserInputBlock.fromJson(block.userInput as JsonValue),
                };
            }
            return {
                id: `output-${block.id}`,
                type: "agent",
                content: AgentResponseBlock.fromJson(block as JsonValue),
            };
        } else if (block.actionStarted?.actionId) {
            // TODO(NEXT-3147): ↑ is a temporary workaround until we've fixed the underlying issue
            return {
                id: `output-${block.actionStarted.actionId}`,
                type: "agent",
                content: AgentResponseBlock.fromJson(block as JsonValue),
            };
        } else if ("payload" in block && "type" in block && isAgentMessageType(block.type)) {
            return {
                id: `agentmsg-${index}`,
                type: "agentMsg",
                content: AgentMessageProto.fromJson(block as JsonValue),
            };
        } else {
            const id = String("id" in block ? block.id : index);
            return {
                id: `input-${id}`,
                type: "user",
                content: UserInputBlock.fromJson(block as JsonValue),
            };
        }
    } catch (e) {
        console.warn("Failed to parse block:", e, line);
        return null;
    }
}

const AGENT_MESSAGE_TYPES = new Set(["TYPE_UNSPECIFIED", "TYPE_UPDATE", "TYPE_COMPLETE"]);

function isAgentMessageType(value: unknown): boolean {
    return typeof value === "string" && AGENT_MESSAGE_TYPES.has(value);
}

/**
 * Hook to stream conversation data from a URL
 */
export function useStreamConversation(
    agentExecutionId?: string,
    url?: string,
    pendingMessages?: PendingMessage[],
): {
    error?: Error;
    isLoading: boolean;
    loadingMessage?: string;
    messages: Message[];
    lastAgentBlockContext?: LastBlockActivityContext;
} {
    const [error, setError] = useState<Error | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState<string | undefined>();

    // Use ref to avoid array copying on each update, reducing GC pressure
    const blocksRef = useRef<ConversationBlock[]>([]);
    const [blocksVersion, setBlocksVersion] = useState(0);

    // We use a generation counter to ignore pushes from stale streams.
    // When a new stream starts, it increments the generation. The old stream's closure
    // captured the old generation value, so its pushes are ignored.
    // This handle a race condition where the conversation token refreshing could have
    // two streams writing to the same block ref.
    const streamGenerationRef = useRef(0);

    const [, setFailureCount] = useState(0);
    const [lastAgentBlockContext, setLastAgentBlockContext] = useState<LastBlockActivityContext | undefined>(undefined);
    const { data: token, error: tokenError } = useCreateAgentExecutionConversationToken(agentExecutionId);

    // Check for URL override in localStorage (development only)
    const overrideUrl = agentExecutionId ? localStorage.getItem(`conversation-url-override-${agentExecutionId}`) : null;
    const effectiveUrl = overrideUrl || url;

    const waitAndRenderRetry = useCallback(async (seconds: number, signal: AbortSignal) => {
        for (let i = seconds; i >= 0 && !signal.aborted; i--) {
            setLoadingMessage(i === 0 ? "Retrying now..." : `Retrying in ${i}s...`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    }, []);

    const streamFromUrl = useCallback(
        async (signal: AbortSignal) => {
            if (!effectiveUrl || !token || !agentExecutionId) return;

            // Increment generation to invalidate any in-flight iterations from previous streams
            const currentGeneration = ++streamGenerationRef.current;

            let error: Error | undefined;

            while (!signal.aborted) {
                try {
                    // Reset state when starting a new fetch
                    setIsLoading(true);
                    blocksRef.current = [];
                    setBlocksVersion(0);
                    setLoadingMessage(undefined);
                    setLastAgentBlockContext(undefined);

                    const cacheKey = getConversationCacheKey(agentExecutionId);
                    const stream = streamWithCache(cacheKey, effectiveUrl, {
                        signal,
                        mode: "cors",
                        keepalive: false,
                        headers: { Authorization: `Bearer ${token}` },
                    });

                    for await (const item of parseNDJSON(stream, signal)) {
                        // Ignore pushes from stale streams - prevents race condition where
                        // old stream's in-flight iteration pushes to the reset array
                        if (currentGeneration !== streamGenerationRef.current) {
                            return;
                        }

                        // Push directly to ref to avoid array copying and GC pressure
                        blocksRef.current.push(...item);
                        setBlocksVersion((v) => v + 1);

                        // Update last agent activity context to we can track if the conversation is stuck.
                        const agentBlocks = item.filter((block) => block.type === "agent");
                        if (agentBlocks.length > 0) {
                            const agentBlockContext: LastBlockActivityContext = {
                                timestamp: Date.now(),
                            };

                            const lastAgentBlock = agentBlocks[agentBlocks.length - 1];
                            if (lastAgentBlock.type === "agent") {
                                agentBlockContext.status = lastAgentBlock.content.output.case;
                                agentBlockContext.id = lastAgentBlock.content.id;
                            }

                            setLastAgentBlockContext(agentBlockContext);
                        }

                        // If we had previously encountered an error, we reset it once
                        // we've successfully parsed a new block.
                        if (error !== undefined) {
                            error = undefined;
                            setError(undefined);
                            setFailureCount(0); // Reset failure count on successful connection
                        }
                    }

                    throw new Error("Stream closed unexpectedly");
                } catch (err) {
                    if (signal.aborted) return;

                    error = err instanceof Error ? err : new Error(String(err));
                    setFailureCount((prev) => {
                        const newCount = prev + 1;
                        // Show error after 3 failed attempts (1-based: attempts 1, 2, 3)
                        if (newCount >= ERROR_DISPLAY_THRESHOLD) {
                            setError(error);
                        }
                        return newCount;
                    });

                    // retry after 3s
                    await waitAndRenderRetry(3, signal);
                } finally {
                    if (!signal.aborted) {
                        setIsLoading(false);
                    }
                }
            }
        },
        [effectiveUrl, token, agentExecutionId, waitAndRenderRetry],
    );

    useEffect(() => {
        const abortController = new AbortController();
        if (effectiveUrl) {
            void streamFromUrl(abortController.signal);
        }

        return () => {
            abortController.abort();
        };
    }, [effectiveUrl, streamFromUrl]);

    const messages = useMemo(() => {
        const { messages } = processBlocks(blocksRef.current, pendingMessages, effectiveUrl);
        return messages;
        // blocksVersion triggers re-computation when new blocks are added
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocksVersion, pendingMessages, effectiveUrl]);

    if (!agentExecutionId) {
        return {
            error: undefined,
            isLoading: false,
            loadingMessage: undefined,
            messages: [],
            lastAgentBlockContext: undefined,
        };
    }

    return {
        error: error ?? (tokenError || undefined),
        isLoading,
        loadingMessage,
        messages,
        lastAgentBlockContext,
    };
}

export function processBlocks(
    blocks: ConversationBlock[],
    pendingMessages?: PendingMessage[],
    conversationUrl?: string,
): {
    messages: Message[];
} {
    const length = blocks.length;
    if (!length) {
        return { messages: [] };
    }

    // make responses unique based on their id, choosing the last entry in the list
    // Using a map + array to keep the order of the blocks
    const idToBlockMap = new Map<string, ConversationBlock>();
    const orderedIds: string[] = [];

    for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        const previous = idToBlockMap.get(block.id);
        if (!previous) {
            orderedIds.push(block.id);
            idToBlockMap.set(block.id, block);
        } else {
            const merged = mergeBlock(previous, block);
            idToBlockMap.set(block.id, merged);
        }

        // Look at the previous block in the list and if it is from an agent, of type text,
        // and not marked as completed, mark it as completed to handle cases where the
        // agent execution didn't mark it as completed itself - happens during interruptions
        if (i > 0) {
            const previousBlock = blocks[i - 1];
            if (
                previousBlock &&
                previousBlock.id !== block.id && // Don't mark the current block as completed
                previousBlock.type === "agent" &&
                previousBlock.content.output.case === "text" &&
                previousBlock.content.phase !== AgentResponseBlock_Phase.COMPLETED
            ) {
                const previousBlockInMap = idToBlockMap.get(previousBlock.id);
                if (previousBlockInMap && previousBlockInMap.type === "agent") {
                    const updatedBlock: ConversationBlock = {
                        ...previousBlockInMap,
                        content: {
                            ...previousBlockInMap.content,
                            phase: AgentResponseBlock_Phase.COMPLETED,
                        },
                    };
                    idToBlockMap.set(previousBlock.id, updatedBlock);
                }
            }
        }
    }

    // More efficient array construction without filter
    const res: ConversationBlock[] = [];
    for (const id of orderedIds) {
        const block = idToBlockMap.get(id);
        if (block) {
            res.push(block);
        }
    }

    // For any pending message that isn't already in the stream we add them to the end of the stream.
    // This acts as a sort of optimistic update, where it will appear like the pending messages are already in the stream.
    for (const pendingMessage of pendingMessages ?? []) {
        // We only want to add the pending messages that are not yet present in the conversation already
        const seenInStream = idToBlockMap.has(`input-${pendingMessage.id}`);
        if (!seenInStream) {
            res.push({
                id: pendingMessage.id,
                type: "user",
                content: new UserInputBlock({
                    input: {
                        case: "text",
                        value: new UserInputBlock_TextInput({ content: pendingMessage.content }),
                    },
                }),
            });
        }
    }

    const messages = convertToMessages(res, conversationUrl);

    return { messages };
}

/*
 * This merges agent blocks of type text to handle delta updates.
 * I want to do something different in the near future, but for now this makes the rest of the UI compatible
 * with the pre-delta agent stream.
 */
function mergeBlock(previous: ConversationBlock, current: ConversationBlock): ConversationBlock {
    // Should never happen, but you never know.
    if (previous.type !== current.type) {
        throw new Error("Cannot merge blocks of different types");
    }

    // No special merging for user or agentMsg blocks
    if (current.type === "user" || current.type === "agentMsg") {
        return current;
    }

    // For some very unfortunate reason, we produced UPDATE blocks using the delta semantics
    // so we need to treat UPDATE blocks as DELTA blocks
    const isDelta =
        current.content.phase === AgentResponseBlock_Phase.UPDATE ||
        current.content.phase === AgentResponseBlock_Phase.DELTA;

    // We only care about merging text blocks
    if (
        // We know this from the check above, but the TS compiler does not.
        previous.type === "agent" &&
        // We only care about updates
        isDelta &&
        // and only if they're text blocks
        current.content.output.case === "text" &&
        previous.content.output.case === "text"
    ) {
        // If the current sequence ID is smaller than the previously seen one, then it means that
        // parts of the conversation is being replayed, so we don't want to update anything.
        if (previous.content.output.value.sequenceId >= current.content.output.value.sequenceId) {
            return previous;
        }

        // From the proto docs: "Starts from 1, with 0 indicating unset (backwards compatible)", so we skip 0
        if (current.content.output.value.sequenceId)
            return {
                ...previous,
                content: {
                    ...previous.content,
                    output: {
                        ...previous.content.output,
                        value: {
                            ...previous.content.output.value,
                            // Update the block with the latest sequence ID
                            sequenceId: current.content.output.value.sequenceId,
                            // Append the delta message to the block
                            content: previous.content.output.value.content + current.content.output.value.content,
                        },
                    },
                },
            };
    }

    // For all other cases, the current block is the new ground truth
    return current;
}
